import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, MAT_TYPE } from "@/lib/ui";
import { toast } from "sonner";
import { Search, Pencil, Trash2, X, Plus, Boxes, Check } from "lucide-react";

const TYPES = [
  { key: "", label: "Tümü" },
  { key: "tel", label: "Tel" },
  { key: "tela", label: "Tela" },
  { key: "kraft", label: "Kraft" },
  { key: "lamet", label: "Lamet" },
  { key: "cring", label: "C-Ring" },
  { key: "holtmelt", label: "Holtmelt" },
  { key: "celik_cember", label: "Çelik Çember" },
  { key: "tahta_palet", label: "Tahta Palet" },
  { key: "round_frame", label: "Round Frame" },
];

export default function Materials() {
  const { isEditor } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ current_stock: "", safety_stock: "", suppliers: [] });
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", type: "tel", unit: "kg", current_stock: "", safety_stock: "" });
  const [savingId, setSavingId] = useState(null);

  const load = () => api.get("/materials").then((r) => setMaterials(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return materials.filter(
      (m) => (!type || m.type === type) && m.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [materials, search, type]);

  // inline quick stock update
  const saveStock = async (m, value) => {
    const v = parseFloat(value);
    if (isNaN(v) || v === m.current_stock) return;
    setSavingId(m.id);
    try {
      await api.put(`/materials/${m.id}`, {
        current_stock: v, safety_stock: m.safety_stock, suppliers: m.suppliers,
      });
      setMaterials((prev) => prev.map((x) => (x.id === m.id ? { ...x, current_stock: v } : x)));
      toast.success(`${m.name} stoğu güncellendi`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSavingId(null);
  };

  const openEdit = (m) => {
    setEdit(m);
    setForm({
      current_stock: m.current_stock, safety_stock: m.safety_stock,
      suppliers: m.suppliers.length ? m.suppliers.map((s) => ({ ...s })) : [{ name: "", lead_time_weeks: "" }],
    });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/materials/${edit.id}`, {
        current_stock: parseFloat(form.current_stock) || 0,
        safety_stock: parseFloat(form.safety_stock) || 0,
        suppliers: form.suppliers.filter((s) => s.name).map((s) => {
          const lt = parseFloat(s.lead_time_weeks);
          return { name: s.name, lead_time_weeks: isNaN(lt) ? 2 : lt };
        }),
      });
      toast.success("Malzeme güncellendi");
      setEdit(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createMaterial = async () => {
    if (!addForm.name.trim()) { toast.error("Malzeme adı gerekli"); return; }
    try {
      await api.post("/materials", {
        name: addForm.name.trim(), type: addForm.type, unit: addForm.unit,
        current_stock: parseFloat(addForm.current_stock) || 0,
        safety_stock: parseFloat(addForm.safety_stock) || 0, suppliers: [],
      });
      toast.success("Malzeme eklendi");
      setAddOpen(false);
      setAddForm({ name: "", type: "tel", unit: "kg", current_stock: "", safety_stock: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (m) => {
    if (!window.confirm(`${m.name} silinsin mi?`)) return;
    try { await api.delete(`/materials/${m.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-in-up" data-testid="materials-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight">Malzemeler / Stok Yönetimi</h1>
          <p className="text-[#94A3B8] text-sm mt-1">
            <span className="font-num">{materials.length}</span> malzeme. Stok kutusuna yazıp Enter'a
            basarak güncel stoğu anında düzenleyin.
          </p>
        </div>
        {isEditor && (
          <button data-testid="material-add-button" onClick={() => setAddOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-sm font-semibold">
            <Plus className="w-4 h-4" /> Yeni Malzeme
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-[#64748B] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="material-search-input" placeholder="Malzeme ara..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-[#131A26] border border-[#212D40] rounded-lg pl-10 pr-4 py-2.5 text-sm outline-none focus:border-[#2563EB]" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {TYPES.map((t) => (
            <button key={t.key} data-testid={`material-type-${t.key || "all"}`} onClick={() => setType(t.key)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                type === t.key ? "bg-[#2563EB] text-white" : "bg-[#131A26] border border-[#212D40] text-[#94A3B8] hover:text-white"
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[#131A26] border border-[#212D40] rounded-xl overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-320px)] overflow-y-auto">
          <table className="w-full text-sm" data-testid="material-inventory-table">
            <thead className="sticky top-0 bg-[#0F1520] z-10">
              <tr className="text-left text-[#94A3B8] text-xs uppercase tracking-wider border-b border-[#212D40]">
                <th className="py-3 px-5">Malzeme</th>
                <th className="py-3 px-5">Tür</th>
                <th className="py-3 px-5 text-right">Güncel Stok</th>
                <th className="py-3 px-5 text-right">Emniyet</th>
                <th className="py-3 px-5">Tedarikçiler</th>
                <th className="py-3 px-5"></th>
              </tr>
            </thead>
            <tbody>
              {[...filtered].sort((a, b) => a.name.localeCompare(b.name, "tr")).map((m) => {
                const t = MAT_TYPE[m.type] || {};
                return (
                  <tr key={m.id} className="border-b border-[#1a2334] hover:bg-[#0F1520]" data-testid={`material-row-${m.id}`}>
                    <td className="py-3 px-5 font-medium flex items-center gap-2">
                      <Boxes className="w-4 h-4" style={{ color: t.color }} /> {m.name}
                    </td>
                    <td className="py-3 px-5">
                      <span className="text-xs px-2 py-0.5 rounded" style={{ color: t.color, background: `${t.color}22` }}>{t.label}</span>
                    </td>
                    <td className="py-3 px-5 text-right">
                      {isEditor ? (
                        <div className="flex items-center justify-end gap-1">
                          <input
                            data-testid={`material-stock-inline-${m.id}`}
                            key={m.current_stock}
                            type="number"
                            defaultValue={m.current_stock}
                            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                            onBlur={(e) => saveStock(m, e.target.value)}
                            className="w-28 bg-[#0F1520] border border-[#212D40] rounded-lg px-2 py-1.5 text-sm text-right font-num outline-none focus:border-[#2563EB]"
                          />
                          <span className="text-xs text-[#64748B] w-8">{m.unit}</span>
                          {savingId === m.id && <Check className="w-4 h-4 text-[#10B981]" />}
                        </div>
                      ) : (
                        <span className="font-num">{fmt(m.current_stock)} {m.unit}</span>
                      )}
                    </td>
                    <td className="py-3 px-5 text-right font-num text-[#94A3B8]">{fmt(m.safety_stock)}</td>
                    <td className="py-3 px-5">
                      <div className="flex flex-wrap gap-1.5">
                        {m.suppliers.length ? m.suppliers.map((s) => (
                          <span key={s.name} className="text-xs bg-[#0F1520] border border-[#212D40] rounded px-2 py-0.5">
                            {s.name} · <span className="font-num">{s.lead_time_weeks}hf</span>
                          </span>
                        )) : <span className="text-xs text-[#64748B]">—</span>}
                      </div>
                    </td>
                    <td className="py-3 px-5 text-right whitespace-nowrap">
                      {isEditor && (
                        <>
                          <button onClick={() => openEdit(m)} data-testid={`material-edit-${m.id}`} className="text-[#64748B] hover:text-[#2563EB] mr-3">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => remove(m)} data-testid={`material-delete-${m.id}`} className="text-[#64748B] hover:text-[#EF4444]">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-[#64748B]">Sonuç yok.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit dialog */}
      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-[#131A26] border border-[#212D40] rounded-xl w-full max-w-lg max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-[#212D40]">
              <h3 className="font-head text-xl font-semibold truncate pr-4">{edit.name}</h3>
              <button onClick={() => setEdit(null)}><X className="w-5 h-5 text-[#94A3B8]" /></button>
            </div>
            <div className="p-5 overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Güncel Stok ({edit.unit})</label>
                  <input data-testid="material-stock-input" type="number" value={form.current_stock}
                    onChange={(e) => setForm({ ...form, current_stock: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Emniyet Stoğu</label>
                  <input type="number" value={form.safety_stock}
                    onChange={(e) => setForm({ ...form, safety_stock: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                </div>
              </div>
              <div>
                <div className="space-y-2 mt-2">
                  {form.suppliers.map((s, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input placeholder="Tedarikçi adı" data-testid={`supplier-name-${idx}`} value={s.name}
                        onChange={(e) => { const sup = [...form.suppliers]; sup[idx].name = e.target.value; setForm({ ...form, suppliers: sup }); }}
                        className="flex-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                      <input placeholder="hafta" data-testid={`supplier-lead-${idx}`} type="number" value={s.lead_time_weeks}
                        onChange={(e) => { const sup = [...form.suppliers]; sup[idx].lead_time_weeks = e.target.value; setForm({ ...form, suppliers: sup }); }}
                        className="w-24 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                      <button onClick={() => setForm({ ...form, suppliers: form.suppliers.filter((_, i) => i !== idx) })}
                        className="text-[#64748B] hover:text-[#EF4444] px-2"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
                <button data-testid="supplier-add-button" onClick={() => setForm({ ...form, suppliers: [...form.suppliers, { name: "", lead_time_weeks: "" }] })}
                  className="mt-2 text-sm text-[#2563EB] hover:underline flex items-center gap-1">
                  <Plus className="w-3.5 h-3.5" /> Tedarikçi ekle
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-[#212D40]">
              <button onClick={() => setEdit(null)} className="px-4 py-2 rounded-lg text-sm text-[#94A3B8] hover:bg-[#0F1520]">İptal</button>
              <button data-testid="material-save-button" onClick={saveEdit} className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#2563EB] hover:bg-[#1D4ED8]">Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Add dialog */}
      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-[#131A26] border border-[#212D40] rounded-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-[#212D40]">
              <h3 className="font-head text-xl font-semibold">Yeni Sarf Malzemesi</h3>
              <button onClick={() => setAddOpen(false)}><X className="w-5 h-5 text-[#94A3B8]" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Malzeme Adı</label>
                <input data-testid="add-material-name" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                  placeholder="Örn. Tel 1,10mm - 0,45C" className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Tür</label>
                  <select data-testid="add-material-type" value={addForm.type} onChange={(e) => setAddForm({ ...addForm, type: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]">
                    <option value="tel">Tel</option>
                    <option value="tela">Tela</option>
                    <option value="kraft">Kraft</option>
                    <option value="lamet">Lamet</option>
                    <option value="cring">C-Ring</option>
                    <option value="holtmelt">Holtmelt</option>
                    <option value="celik_cember">Çelik Çember</option>
                    <option value="tahta_palet">Tahta Palet</option>
                    <option value="round_frame">Round Frame</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Birim</label>
                  <input value={addForm.unit} onChange={(e) => setAddForm({ ...addForm, unit: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Başlangıç Stok</label>
                  <input data-testid="add-material-stock" type="number" value={addForm.current_stock} onChange={(e) => setAddForm({ ...addForm, current_stock: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Emniyet Stoğu</label>
                  <input type="number" value={addForm.safety_stock} onChange={(e) => setAddForm({ ...addForm, safety_stock: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-[#212D40]">
              <button onClick={() => setAddOpen(false)} className="px-4 py-2 rounded-lg text-sm text-[#94A3B8] hover:bg-[#0F1520]">İptal</button>
              <button data-testid="add-material-save" onClick={createMaterial} className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#2563EB] hover:bg-[#1D4ED8]">Ekle</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
